import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import { RiArrowDownSLine } from 'react-icons/ri';

import { Skeleton } from 'components';

export default function Sorting({ pathname, search, push, isLoading }) {
  const [sortingMethods] = useState(
    /** @type {{name: string, value: string}[]} sortingMethods - The Sorting Methods */
    [
      {
        name: t`Popular`,
        value: ''
      },
      {
        name: t`Newest Products`,
        value: 'latest-online'
      },
      {
        name: t`Biggest Discount`,
        value: 'discount-desc'
      },
      {
        name: t`Price (Lowest)`,
        value: 'price-asc'
      },
      {
        name: t`Price (Highest)`,
        value: 'price-desc'
      },
      {
        name: `A-Z`,
        value: 'name-abc'
      }
    ]
  );

  /**
   * Returns the Sorting Method from the URL.
   * @type {(function(): string)}
   */
  const getSortingMethod = useCallback(() => {
    const searchParams = new URLSearchParams(search);
    const sortingMethod = searchParams.get('sort');
    return (
      sortingMethods.find(method => method.value === sortingMethod) ||
      sortingMethods[0]
    );
  }, [search]);

  const [selectedSortingMethod, setSelectedSortingMethod] = useState(
    /** @type {string} selectedSortingMethod - The currently selected Sorting Method */
    getSortingMethod()
  );

  /**
   * Sets the Sorting Method when the URL changes.
   * @type {React.useEffect<void>}
   */
  useEffect(() => {
    setSelectedSortingMethod(getSortingMethod());
  }, [pathname]);

  /**
   * Sets the Sorting Method and updates the URL.
   * @param {string} value - The value of the Sorting Method. See sortingMethods.
   * @type {(function(string): void)}
   */
  const setSortingMethod = useCallback(value => {
    setSelectedSortingMethod(
      sortingMethods.find(method => method.value === value)
    );
    const url = new URL(window.location.href);

    url.searchParams.delete('page');

    if (value === '') {
      url.searchParams.delete('sort');
    } else {
      url.searchParams.set('sort', value);
    }
    push(url);
  }, []);

  return (
    <Skeleton isLoading={isLoading} width={160} height={38}>
      <div className="relative sm:w-[160px]">
        <label htmlFor="sorting" className="sr-only">
          {t`Sorting`}
        </label>
        <select
          id="sorting"
          value={selectedSortingMethod.value}
          onChange={event => setSortingMethod(event.target.value)}
          className="flex w-full cursor-pointer appearance-none items-center justify-between space-x-2 border border-solid border-gray bg-white px-2 py-2 text-left text-sm text-black focus:outline-none"
        >
          {sortingMethods.map(sortingMethod => (
            <option key={sortingMethod.value} value={sortingMethod.value}>
              {sortingMethod.name}
            </option>
          ))}
        </select>
        <RiArrowDownSLine className="absolute top-1/2 right-2 h-4 w-4 shrink-0 -translate-y-1/2 transform text-dark-gray" />
      </div>
    </Skeleton>
  );
}

Sorting.propTypes = {
  /**
   * The Pathname of the current Page.
   */
  pathname: PropTypes.string.isRequired,
  /**
   * The Search Query of the current Page.
   */
  search: PropTypes.string.isRequired,
  /**
   * The Push function from the useLocation hook.
   */
  push: PropTypes.func.isRequired,
  /**
   * Whether the Data is currently being loaded or not.
   */
  isLoading: PropTypes.bool
};

Sorting.defaultProps = {
  isLoading: false
};
